import React, { useEffect, useState, useRef, useMemo, Suspense, lazy } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SeniorLayout } from './components/layout/SeniorLayout';
import { HomeRouter } from './components/HomeRouter';
import { Home } from './pages/Home';
import { AuthPage } from './pages/Auth';
import { CareChat } from './pages/CareChat';
import { LanguageEngine } from './lib/LanguageEngine';
import { ErrorBoundary, PageErrorBoundary } from './components/ui/ErrorBoundary';
import { UpdatePrompt, PushNotificationPrompt } from './components/ui/UpdatePrompt';
import { AnnouncementProvider, SkipLink } from './components/ui/Accessibility';
import { supabase } from './lib/supabase';
import { ResidentsPage } from './pages/admin/ResidentsPage';
const PointOfCarePage = lazy(() => import('./pages/admin/PointOfCarePage'));
import { CareNotesPage } from './pages/admin/CareNotesPage';
import { RiskDashboard } from './pages/admin/RiskDashboard';
import { FacilitySettingsPage } from './pages/admin/FacilitySettingsPage';
import { EscalationRulesPage } from './pages/admin/EscalationRulesPage';
import { ResidentProfilePage } from './pages/admin/ResidentProfilePage';
import { StaffPage } from './pages/admin/StaffPage';
import { ReportsPage } from './pages/admin/ReportsPage';
import { HCAResourcePage } from './pages/admin/HCAResourcePage';
import { CareFollowThroughPage } from './pages/admin/CareFollowThroughPage';
import { PricingPage } from './pages/public/PricingPage';
import RoleSelector from './pages/RoleSelector';
import { AdminGuard, SeniorGuard } from './components/RouteGuard';
import FamilyConnect from './pages/family/FamilyConnect';
import CareTimeline from './pages/family/CareTimeline';
import { useTenant } from './store/TenantContext';
import FacilityNotFound from './pages/FacilityNotFound';


// Security Overlay Component - requires user verification before showing authh form
const SecurityOverlay: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [verified, setVerified] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);
  
  const handleVerify = () => {
    setVerified(true);
  };

  return (
    <div className="relative">
      {/* Actual auth content - always rendered, visibility controlled */}
      <div className={`transition-all duration-500 ${verified ? 'opacity-100' : 'blur-sm opacity-40 pointer-events-none select-none'}`}>
        {children}
      </div>
      
      {/* Security overlay - only shown when not verified */}
      {!verified && (
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer group z-10"
          onClick={handleVerify}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
          aria-label="Click to verify you're human and access the registration form"
        >
          {/* Gradient background that matches the theme */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/98 via-surface/98 to-primary/10 rounded-2xl border-2 border-dashed border-emerald-300/60 transition-all duration-300 group-hover:border-emerald-400 group-hover:shadow-lg backdrop-blur-sm" />
          
          {/* Shield content */}
          <div className="relative z-10 text-center p-6 transition-transform duration-300 group-hover:scale-105">
            {/* Animated shield icon */}
            <div className={`mx-auto mb-3 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-500 ${
              isHovering 
                ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-xl shadow-emerald-300/50' 
                : 'bg-gradient-to-br from-emerald-300 to-emerald-500 shadow-lg shadow-emerald-200/30'
            }`}>
              <ShieldCheck 
                size={28} 
                className={`text-white transition-all duration-300 ${isHovering ? 'scale-110' : ''}`} 
              />
            </div>
            
            {/* Trust badge text */}
            <div className="space-y-2">
              <h3 className="text-xl font-bold text-gray-800 flex items-center justify-center gap-2">
                <span>🔒</span> Security Check
              </h3>
              <p className="text-gray-600 font-medium text-sm max-w-xs mx-auto">
                For your safety, please verify you're human before registering
              </p>
              
              {/* Click prompt with animation */}
              <div className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
                isHovering 
                  ? 'bg-emerald-500 text-white shadow-lg' 
                  : 'bg-emerald-100 text-gray-800'
              }`}>
                <span className="font-semibold text-sm">
                  {isHovering ? '✓ Click to Verify' : 'Tap to Continue'}
                </span>
                {!isHovering && (
                  <span className="animate-bounce">👆</span>
                )}
              </div>
            </div>
            
            {/* Trust indicators */}
            <div className="mt-3 flex items-center justify-center gap-3 text-xs text-black/70">
              <span className="flex items-center gap-1">🛡️ Secure</span>
              <span className="flex items-center gap-1">✅ Verified</span>
              <span className="flex items-center gap-1">🔐 Protected</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Chunk load error fallback component
const ChunkLoadErrorFallback: React.FC<{ pageName?: string }> = ({ pageName = 'Page' }) => (
  <div className="flex flex-col items-center justify-center min-h-[300px] p-8 text-center">
    <div className="w-16 h-16 mb-4 text-amber-500">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
    </div>
    <h2 className="text-xl font-semibold text-gray-800 mb-2">Failed to Load</h2>
    <p className="text-gray-600 mb-4">
      {pageName} couldn't be loaded. Please check your internet connection.
    </p>
    <button
      onClick={() => window.location.reload()}
      className="px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors"
    >
      Refresh Page
    </button>
  </div>
);

// Helper to create lazy components with chunk error handling
const lazyWithRetry = <T extends React.ComponentType<any>>(
  importFn: () => Promise<{ [key: string]: T }>,
  componentName: string,
  pageName?: string
) => {
  return lazy(() =>
    importFn()
      .then(module => ({ default: module[componentName] as T }))
      .catch(() => ({ default: (() => <ChunkLoadErrorFallback pageName={pageName} />) as unknown as T }))
  );
};

// Lazy load pages with preloading and chunk error handling
const Chat = lazyWithRetry(() => import('./pages/Chat'), 'Chat', 'Chat');
const Games = lazyWithRetry(() => import('./pages/Games'), 'Games', 'Games');
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'), 'Dashboard', 'Dashboard');
const Reminders = lazyWithRetry(() => import('./pages/Reminders'), 'Reminders', 'Reminders');
const Mood = lazyWithRetry(() => import('./pages/Mood'), 'Mood', 'Mood');
const Exercises = lazyWithRetry(() => import('./pages/Exercises'), 'Exercises', 'Exercises');
const About = lazyWithRetry(() => import('./pages/About'), 'About', 'About');
const Settings = lazyWithRetry(() => import('./pages/Settings'), 'Settings', 'Settings');
const DailyWisdom = lazyWithRetry(() => import('./pages/DailyWisdom'), 'DailyWisdom', 'Daily Wisdom');
const HealthResources = lazyWithRetry(() => import('./pages/HealthResources'), 'HealthResources', 'Health Resources');
const FamilySharing = lazyWithRetry(() => import('./pages/FamilySharing'), 'FamilySharing', 'Family Sharing');
const Hobbies = lazyWithRetry(() => import('./pages/Hobbies'), 'Hobbies', 'Hobbies');
const Privacy = lazyWithRetry(() => import('./pages/Privacy'), 'Privacy', 'Privacy Policy');
const Terms = lazyWithRetry(() => import('./pages/Terms'), 'Terms', 'Terms of Service');
const VoiceTutorial = lazyWithRetry(() => import('./pages/VoiceTutorial'), 'VoiceTutorial', 'Voice Tutorial');
const SignIn = lazyWithRetry(() => import('./pages/SignIn'), 'SignIn', 'Sign In');
const AcceptInvite = lazyWithRetry(() => import('./pages/AcceptInvite'), 'AcceptInvite', 'Accept Invite');
const AdminDashboard = lazyWithRetry(() => import('./pages/admin/AdminDashboard'), 'AdminDashboard', 'Admin Dashboard');
const AdminLayout = lazyWithRetry(() => import('./pages/admin/AdminLayout'), 'AdminLayout', 'Admin Layout');
const WorkerApp = lazyWithRetry(() => import('./pages/worker/WorkerApp'), 'WorkerApp', 'Worker App');

// Preload critical pages after initial render for instant navigation
const preloadCriticalPages = () => {
  // Preload nav bar pages immediately
  import('./pages/About');
  import('./pages/Settings');
  
  // Preload other frequently used pages after a short delay
  setTimeout(() => {
    import('./pages/Chat');
    import('./pages/Games');
    import('./pages/Reminders');
    import('./pages/DailyWisdom');
  }, 1000);
  
  // Preload less frequently used pages after longer delay
  setTimeout(() => {
    import('./pages/Mood');
    import('./pages/Exercises');
    import('./pages/HealthResources');
    import('./pages/FamilySharing');
    import('./pages/Hobbies');
  }, 3000);
};
import { LanguageProvider, useTheme } from './i18n/LanguageContext';
import { MessageCircle, Brain, Heart, Bell, Shield, Users, Dumbbell, Sparkles, Phone, AlertTriangle, Lock, HelpCircle, ChevronRight, Star, Sun, Moon, Monitor, ShieldCheck } from 'lucide-react';
import { PageSkeleton, ReminderSkeleton, FullPageSkeleton } from './components/ui/Skeleton';

// Loading fallback component - now using skeleton screens
const PageLoader = ({ type = 'default' }: { type?: 'default' | 'cards' | 'chat' | 'games' | 'reminders' }) => {
  switch (type) {
    case 'chat':
      return <FullPageSkeleton type="chat" />;
    case 'games':
      return <FullPageSkeleton type="games" />;
    case 'cards':
      return <FullPageSkeleton type="cards" />;
    case 'reminders':
      return (
        <div className="p-4 space-y-4">
          <div className="h-8 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded w-1/3 animate-pulse mb-2" />
          <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded w-1/2 animate-pulse mb-6" />
          <ReminderSkeleton count={4} />
        </div>
      );
    default:
      return <PageSkeleton message="Loading..." />;
  }
};

// Feature data for landing page - will be memoized in component
const featureData = [
  {
    icon: MessageCircle,
    title: "Friendly AI Chat",
    description: "A patient, understanding companion who listens without judgment. Share stories, ask questions, or just chat about your day.",
    color: "text-primary"
  },
  {
    icon: Brain,
    title: "Brain Games",
    description: "Fun puzzles and memory games designed to keep your mind sharp. Play at your own pace, no pressure!",
    color: "text-secondary"
  },
  {
    icon: Bell,
    title: "Gentle Reminders",
    description: "Never miss medications, appointments, or important events. We'll remind you kindly when it's time.",
    color: "text-water"
  },
  {
    icon: Heart,
    title: "Mood & Wellness",
    description: "Track how you're feeling and get personalized suggestions for staying happy and healthy.",
    color: "text-emergency"
  },
  {
    icon: Dumbbell,
    title: "Chair Exercises",
    description: "Safe, gentle exercises you can do from home. Stay active and strong at your own comfort level.",
    color: "text-exercise"
  },
  {
    icon: Users,
    title: "Family Connection",
    description: "Share wellness updates with loved ones. Let family check in on your wellbeing from anywhere.",
    color: "text-accent"
  },
  {
    icon: Shield,
    title: "Emergency Help",
    description: "Quick access to emergency services and health resources. One tap to reach help when needed.",
    color: "text-emergency"
  },
  {
    icon: HelpCircle,
    title: "Voice Commands",
    description: "Control the app hands-free with simple voice commands. Say 'Help' to learn all available commands.",
    color: "text-water"
  },
  {
    icon: Sparkles,
    title: "Daily Wisdom",
    description: "Start each day with inspiring quotes and thoughtful reflections to brighten your morning.",
    color: "text-secondary"
  }
];

// Landing Page Component - Matches SeniorLayout structure exactly
const LandingPage = ({ onAuthSuccess }: { onAuthSuccess: (user: any) => void }) => {
  const [showAuth, setShowAuth] = useState(false);
  const { theme, setTheme } = useTheme();
  const authSectionRef = useRef<HTMLDivElement>(null);
  
  // Memoize features array for performance
  const features = useMemo(() => featureData, []);

  const scrollToAuth = () => {
    setShowAuth(true);
    setTimeout(() => {
      authSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div 
      className="min-h-[100dvh] gradient-warm flex flex-col max-w-md mx-auto shadow-2xl overflow-hidden border-x border-accent/20"
      style={{ contain: 'layout' }}
    >
      {/* Header - Matches SeniorLayout header exactly */}
      <header className="bg-surface/80 backdrop-blur-sm p-3 border-b border-accent/20 flex items-center justify-between sticky top-0 z-10 rounded-b-3xl shadow-warm">
        <div>
          <h1 className="header-text font-bold text-primary font-serif" style={{ fontSize: '2.2rem' }}>DIPPS</h1>
        </div>
        
        {/* Emergency Button */}
        <a 
          href="tel:911"
          className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-lg transition-colors"
          aria-label="Emergency 911"
        >
          <Phone size={18} strokeWidth={2.5} />
          <span className="text-sm font-bold">911</span>
        </a>
      </header>

      {/* Main Content - Matches SeniorLayout main area */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 pb-8 w-full">
        <div className="space-y-6">
          {/* Hero Section */}
          <div className="card-warm text-center">
            <div className="mb-4">
              <p className="text-accent text-sm mb-1">Welcome to</p>
              <h2 className="text-senior-xl font-bold text-primary font-serif">
                Your Caring Companion
              </h2>
              <p className="text-secondary font-medium text-sm mt-2 italic">
                Smart Care for Real Lives
              </p>
            </div>

            <p className="text-text-secondary text-sm leading-relaxed mb-4">
              Your patient, understanding companion — here to chat, help you remember things, and keep your mind active.
            </p>
            
            <button
              onClick={scrollToAuth}
              className="w-full bg-primary text-white px-6 py-4 rounded-2xl text-lg font-bold shadow-lg hover:bg-primary/90 transition-all"
              aria-label="Get started for free"
            >
              Get Started — It's Free!
            </button>
          </div>

          {/* Features Section */}
          <div className="card-warm">
            <h2 className="text-lg font-bold text-primary font-serif text-center mb-4">
              Everything You Need
            </h2>

            <div className="space-y-3">
              {features.slice(0, 6).map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 rounded-2xl bg-background/50"
                >
                  <div className={`p-2 rounded-xl bg-background ${feature.color}`}>
                    <feature.icon size={22} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-primary">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-text-secondary leading-relaxed">
                      {feature.description.split('.')[0]}.
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonials Section */}
          <div className="card-warm">
            <h2 className="text-lg font-bold text-primary font-serif text-center mb-4">
              What Users Say ❤️
            </h2>

            <div className="space-y-3">
              <div className="p-4 rounded-2xl bg-background/50">
                <div className="flex gap-1 mb-2">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="text-yellow-400" size={16} fill="currentColor" />
                  ))}
                </div>
                <p className="text-text-secondary text-sm leading-relaxed mb-2">
                  "Finally, something I can use without asking my grandkids for help!"
                </p>
                <p className="font-bold text-primary text-sm">— Margaret, 78</p>
              </div>

              <div className="p-4 rounded-2xl bg-background/50">
                <div className="flex gap-1 mb-2">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="text-yellow-400" size={16} fill="currentColor" />
                  ))}
                </div>
                <p className="text-text-secondary text-sm leading-relaxed mb-2">
                  "The brain games keep me sharp! Love the word scramble."
                </p>
                <p className="font-bold text-primary text-sm">— Robert, 82</p>
              </div>

              <div className="p-4 rounded-2xl bg-background/50">
                <div className="flex gap-1 mb-2">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="text-yellow-400" size={16} fill="currentColor" />
                  ))}
                </div>
                <p className="text-text-secondary text-sm leading-relaxed mb-2">
                  "The name made me laugh out loud! 😂 Love the humor!"
                </p>
                <p className="font-bold text-primary text-sm">— Joan, 74</p>
              </div>
            </div>

            <div className="mt-4 text-center">
              <div className="inline-flex items-center gap-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-full px-4 py-2">
                <span className="text-sm font-bold text-yellow-700 dark:text-yellow-400">⭐ 4.9/5</span>
                <span className="text-text-muted text-sm">from 500+ users</span>
              </div>
            </div>
          </div>

          {/* Emergency Section */}
          <div className="card-warm bg-emergency/5 border-emergency/20">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-xl bg-emergency/10">
                <Phone className="text-emergency" size={24} />
              </div>
              <h3 className="text-lg font-bold text-emergency">Emergency Help</h3>
            </div>
            <p className="text-text-secondary text-sm">
              Quick access to <strong>911</strong>, health info lines, and crisis support. Help is always one button away.
            </p>
          </div>

          {/* Important Info Section */}
          <div className="card-warm">
            <h2 className="text-lg font-bold text-primary font-serif text-center mb-4">
              Important Info
            </h2>
            
            <div className="space-y-4">
              {/* Medical Disclaimer */}
              <div className="p-4 rounded-2xl bg-emergency/5 border border-emergency/20">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="text-emergency" size={20} />
                  <h4 className="text-base font-bold text-emergency">Medical Disclaimer</h4>
                </div>
                <p className="text-sm text-text-secondary">
                  This is <strong>NOT a medical device</strong>. Always consult real doctors for health concerns. In emergencies, call 911.
                </p>
              </div>

              {/* What It Is */}
              <div className="p-4 rounded-2xl bg-background/50">
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="text-secondary" size={20} />
                  <h4 className="text-base font-bold text-secondary">What This App Is</h4>
                </div>
                <div className="text-sm text-text-secondary space-y-1">
                  <p>✓ A friendly reminder tool</p>
                  <p>✓ A companion for casual chats</p>
                  <p>✓ Fun brain games to stay sharp</p>
                  <p>✓ 100% free — supported by donations</p>
                </div>
              </div>

              {/* Privacy */}
              <div className="p-4 rounded-2xl bg-background/50">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="text-water" size={20} />
                  <h4 className="text-base font-bold text-water">Your Privacy</h4>
                </div>
                <p className="text-sm text-text-secondary">
                  We collect <strong>zero personal info</strong>. Your data stays on your device. No tracking, no ads.
                </p>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="card-warm">
            <h2 className="text-lg font-bold text-primary font-serif text-center mb-4">
              FAQ
            </h2>

            <div className="space-y-3">
              <details className="group rounded-2xl bg-background/50 overflow-hidden">
                <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                  <span className="font-semibold text-base text-primary">Is it really free?</span>
                  <ChevronRight className="text-text-muted group-open:rotate-90 transition-transform" size={20} />
                </summary>
                <div className="px-4 pb-4 text-sm text-text-secondary">
                  Yes, 100% free! No hidden fees, no subscriptions, no ads.
                </div>
              </details>

              <details className="group rounded-2xl bg-background/50 overflow-hidden">
                <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                  <span className="font-semibold text-base text-primary">Is it safe to use?</span>
                  <ChevronRight className="text-text-muted group-open:rotate-90 transition-transform" size={20} />
                </summary>
                <div className="px-4 pb-4 text-sm text-text-secondary">
                  Absolutely! We don't collect personal info or track you. Your data stays on your device.
                </div>
              </details>

              <details className="group rounded-2xl bg-background/50 overflow-hidden">
                <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                  <span className="font-semibold text-base text-primary">Can this replace my doctor?</span>
                  <ChevronRight className="text-text-muted group-open:rotate-90 transition-transform" size={20} />
                </summary>
                <div className="px-4 pb-4 text-sm text-text-secondary">
                  <strong className="text-emergency">No.</strong> This is a companion app for chats and reminders. Always consult real doctors for health concerns.
                </div>
              </details>

              <details className="group rounded-2xl bg-background/50 overflow-hidden">
                <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                  <span className="font-semibold text-base text-primary">Why "DIPPS"?</span>
                  <ChevronRight className="text-text-muted group-open:rotate-90 transition-transform" size={20} />
                </summary>
                <div className="px-4 pb-4 text-sm text-text-secondary">
                  DIPPS stands for <strong>Dignity, Independence, Preferences, Privacy, and Safety</strong> — the five core principles of compassionate, person-centred care taught to Personal Support Workers. It's the foundation of how we treat our elders. 💚
                </div>
              </details>
            </div>
          </div>

          {/* Sign Up Section */}
          <div className="card-warm" id="auth-section" ref={authSectionRef}>
            <div className="text-center mb-4">
              <h2 className="text-lg font-bold text-primary font-serif mb-1">
                Join Our Community
              </h2>
              <p className="text-text-secondary text-sm">
                Free to use. No credit card needed.
              </p>
            </div>
            
            <SecurityOverlay>
              <AuthPage onAuthSuccess={onAuthSuccess} />
            </SecurityOverlay>
          </div>

          {/* Footer */}
          <div className="text-center py-4 text-text-muted text-sm space-y-4">
            <button
              onClick={() => setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface border border-accent/20 text-primary shadow-warm text-sm"
              aria-label="Change display mode"
            >
              {theme === 'light' && <Sun size={16} />}
              {theme === 'dark' && <Moon size={16} />}
              {theme === 'system' && <Monitor size={16} />}
              <span className="font-medium">Display</span>
              <span className="text-text-muted">
                ({theme === 'system' ? 'Auto' : theme === 'dark' ? 'Dark' : 'Light'})
              </span>
            </button>
            <p>Made with ❤️ in BC, Canada</p>
            <p>Designed for seniors, by someone who gets it.</p>
            <a href="mailto:support@dipps.ca" className="text-xs text-text-muted hover:text-primary transition-colors">
              support@dipps.ca
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { isCareSubdomain, notFound, loading: tenantLoading } = useTenant();

  // Initialize Language Engine on app startup and preload pages
  useEffect(() => {
    LanguageEngine.initialize();
    
    // Preload critical pages for instant navigation
    preloadCriticalPages();
    
    // Restore Google Translate cookie from backup if available (after auth redirect)
    const googtransBackup = sessionStorage.getItem('googtrans_backup');
    if (googtransBackup && user) {
      // Restore the cookie
      const cookieValue = decodeURIComponent(googtransBackup);
      document.cookie = `googtrans=${cookieValue}; path=/`;
      document.cookie = `googtrans=${cookieValue}; path=/; domain=${window.location.hostname}`;
      sessionStorage.removeItem('googtrans_backup');
      console.log('[App] Restored Google Translate cookie:', cookieValue);
      
      // Reload to apply translation
      if (cookieValue && cookieValue !== '/en/en') {
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    }
  }, [user]);

  useEffect(() => {
    async function checkAuth() {
      try {
        if (!supabase) throw new Error('Supabase not configured');
        const { data: { session } } = await supabase!.auth.getSession();
        setUser(session?.user ?? null);
      } catch (error) {
        console.error('Auth error:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    }

    checkAuth();

    const { data: { subscription } } = supabase!.auth.onAuthStateChange((_event: any, session: any) => {
      setUser(session?.user ?? null);
      if (!session?.user) setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleAuthSuccess = (authUser: any) => {
    setUser(authUser);
    window.location.hash = '';
  };

  // Tenant check: unknown facility subdomain shows FacilityNotFound before auth
  if (isCareSubdomain && !tenantLoading && notFound) {
    return <FacilityNotFound />;
  }
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gradient-warm">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
        <p className="mt-4 text-text-secondary font-serif">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <ErrorBoundary>
        <LanguageProvider>
          <UpdatePrompt />
          <PushNotificationPrompt />
          <LandingPage onAuthSuccess={handleAuthSuccess} />
        </LanguageProvider>
      </ErrorBoundary>
    );
  }


    
  return (
    <ErrorBoundary>
      <LanguageProvider>
                  <AnnouncementProvider>
          <SkipLink targetId="main-content" />
          <UpdatePrompt />
          <PushNotificationPrompt />
          <HashRouter>
            <Routes>
                              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/select-role" element={<RoleSelector />} />
              <Route path="/family/connect" element={<FamilyConnect />} />
                  <Route path="/family/timeline" element={<CareTimeline />} />
                        {/* Admin dashboard route */}
                                    <Route path="/admin" element={
                                      <AdminGuard>
                                                      <Suspense fallback={<PageLoader />}>
                                                                        <AdminLayout>
                                                                                            <AdminDashboard />
                                                                        </AdminLayout>
                                                      </Suspense>
                                      </AdminGuard>
                                      } />
              <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
                    <Route path="/admin/residents" element={<Suspense fallback={<PageLoader />}><AdminLayout><ResidentsPage /></AdminLayout></Suspense>} />
                                                      <Route path="/admin/risk" element={
                                                      <Suspense fallback={<PageLoader />}>
                                                                        <AdminLayout>
                                                                                            <RiskDashboard />
                                                                        </AdminLayout>
                                                      </Suspense>
                                                      } />
                                                      <Route path="/admin/care-notes" element={
                                                      <Suspense fallback={<PageLoader />}>
                                                                        <AdminLayout>
                                                                                            <CareNotesPage />
                                                                        </AdminLayout>
                                                      </Suspense>
                                                      } />
                                                      <Route path="/admin/facility-settings" element={
                                                      <Suspense fallback={<PageLoader />}>
                                                                        <AdminLayout>
                                                                                          <FacilitySettingsPage />
                                                                        </AdminLayout>
                                                      </Suspense>
                                                      } />
                                                      <Route path="/admin/escalation" element={
                                                      <Suspense fallback={<PageLoader />}>
                                                                        <AdminLayout>
                                                                                          <EscalationRulesPage />
                                                                        </AdminLayout>
                                                      </Suspense>
                                                      } />
                                                      <Route path="/admin/residents/:residentId" element={
                                                      <Suspense fallback={<PageLoader />}>
                                                                        <AdminLayout>
                                                                                          <ResidentProfilePage />
                                                                        </AdminLayout>
                                                      </Suspense>
                                                      } />
                                                      <Route path="/admin/staff" element={
                                                      <Suspense fallback={<PageLoader />}>
                                                                        <AdminLayout>
                                                                                          <StaffPage />
                                                                        </AdminLayout>
                                                      </Suspense>
                                                      } />
                                                      <Route path="/admin/reports" element={
                                                        <Suspense fallback={<PageLoader />}>
                                                          <AdminLayout>
                                                            <ReportsPage />
                                                          </AdminLayout>
                                                        </Suspense>
                                                      } />
                                                      <Route path="/admin/hca-resource" element={
                                                        <Suspense fallback={<PageLoader />}>
                                                          <AdminLayout>
                                                            <HCAResourcePage />
                                                          </AdminLayout>
                                                        </Suspense>
                                                      } />
                <Route path="/admin/care-follow-through" element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminLayout>
                      <CareFollowThroughPage />
                    </AdminLayout>
                  </Suspense>
                } />
              <Route path="/admin/poc" element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminLayout>
                      <PointOfCarePage />
                    </AdminLayout>
                  </Suspense>
                } />
              <Route path="/" element={<HomeRouter />} />
              <Route path="/senior" element={<SeniorGuard><SeniorLayout /></SeniorGuard>}>
                <Route index element={<PageErrorBoundary><Home /></PageErrorBoundary>} />
              <Route path="chat" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader type="chat" />}>
                    <Chat />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="reminders" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader type="reminders" />}>
                    <Reminders />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="mood" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Mood />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="exercises" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Exercises />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="games" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader type="games" />}>
                    <Games />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="about" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <About />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="settings" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Settings />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="wisdom" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader type="cards" />}>
                    <DailyWisdom />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="health-resources" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader type="cards" />}>
                    <HealthResources />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="family" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <FamilySharing />
                  </Suspense>
                </PageErrorBoundary>
              } />
                                  <Route path="chat-family" element={<CareChat />} />
              <Route path="hobbies" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Hobbies />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="privacy" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Privacy />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="terms" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Terms />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="dashboard" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Dashboard />
                  </Suspense>
                </PageErrorBoundary>
              } />
              <Route path="voice-tutorial" element={
                <PageErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <VoiceTutorial />
                  </Suspense>
                </PageErrorBoundary>
              } />
            </Route>
                          <Route path="sign-in" element={
              <PageErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <SignIn />
                </Suspense>
              </PageErrorBoundary>
            } />
            <Route path="accept-invite" element={
              <PageErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <AcceptInvite />
                </Suspense>
              </PageErrorBoundary>
            } />
              <Route path="/worker/*" element={<Suspense fallback={<PageLoader />}><WorkerApp /></Suspense>} />
          </Routes>
        </HashRouter>
        </AnnouncementProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
